import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, User, Phone, MapPin, Building, Save, LogOut, Shield } from 'lucide-react';
import CustomerBottomNav from '@/components/CustomerBottomNav';
import CustomerSidebar from '@/components/CustomerSidebar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerProfile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [profile, setProfile] = useState({ full_name: '', phone: '', address: '', pg_name: '' });

  const token = localStorage.getItem('customerToken');

  useEffect(() => {
    if (!token) { navigate('/customer/login'); return; }
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API}/customer/me`, { headers: { Authorization: `Bearer ${token}` } });
      setProfile(res.data);
    } catch (err) {
      if (err.response?.status === 401) { navigate('/customer/login'); return; }
      toast.error('Failed to load profile');
    } finally { setLoading(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.put(`${API}/customer/profile`,
        { full_name: profile.full_name, phone: profile.phone, address: profile.address },
        { headers: { Authorization: `Bearer ${token}` } });
      setProfile(res.data);
      localStorage.setItem('customerData', JSON.stringify(res.data));
      toast.success('Profile updated successfully');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update profile'); }
    finally { setSaving(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem('customerToken');
    localStorage.removeItem('customerData');
    navigate('/customer/login');
  };

  const inputClass = "w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 hover:border-blue-300 focus:border-blue-500 rounded-xl text-slate-800 placeholder-slate-400 text-sm transition-colors outline-none";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const initials = profile.full_name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || 'U';

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden" style={{ fontFamily: 'Inter, sans-serif' }}>
      <CustomerSidebar onLogout={handleLogout} />

      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile topbar */}
        <header className="md:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 flex-shrink-0 z-10">
          <button onClick={() => navigate('/customer/dashboard')}
            className="w-9 h-9 flex items-center justify-center rounded-xl text-slate-500 hover:bg-slate-100 transition-colors">
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <span className="font-black text-slate-900 text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>My Profile</span>
        </header>

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-2xl mx-auto px-4 md:px-8 py-6 pb-24 md:pb-8 space-y-5">

            {/* Desktop page title */}
            <div className="hidden md:block">
              <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>My Profile</h1>
              <p className="text-sm text-slate-500 mt-0.5">Manage your personal information</p>
            </div>

            {/* ── Avatar + PG banner ── */}
            <div className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-violet-700 rounded-[2rem] px-8 py-7 text-white overflow-hidden shadow-xl shadow-blue-200">
              <div className="absolute -right-8 -top-8 w-40 h-40 bg-white/10 rounded-full" />
              <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
              <div className="relative z-10 flex items-center gap-5">
                <div className="w-16 h-16 rounded-2xl bg-white/20 border-2 border-white/30 flex items-center justify-center text-2xl font-black flex-shrink-0" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {initials}
                </div>
                <div>
                  <p className="text-blue-100 text-xs font-medium mb-0.5">Logged in as</p>
                  <h2 className="text-xl font-black mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>{profile.full_name}</h2>
                  <div className="flex items-center gap-2">
                    <Building className="w-3.5 h-3.5 text-blue-200" />
                    <span className="text-blue-100 text-sm font-medium">{profile.pg_name}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ── PG info (read-only) ── */}
            <div className="bg-white border border-slate-200/80 rounded-[2rem] px-6 py-5 shadow-sm flex items-center gap-4">
              <div className="w-12 h-12 bg-violet-50 rounded-xl flex items-center justify-center flex-shrink-0">
                <Building className="w-6 h-6 text-violet-600" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Your PG / Co-living</p>
                <p className="font-bold text-slate-800">{profile.pg_name}</p>
              </div>
              <div className="flex items-center gap-1.5 bg-violet-50 border border-violet-100 rounded-xl px-3 py-1.5">
                <Shield className="w-3 h-3 text-violet-500" />
                <span className="text-[10px] font-bold text-violet-600">Verified</span>
              </div>
            </div>

            {/* ── Edit form ── */}
            <div className="bg-white border border-slate-200/80 rounded-[2rem] px-6 py-6 shadow-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                  <User className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-black text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Personal Details</h3>
                  <p className="text-xs text-slate-400">Update your information below</p>
                </div>
              </div>

              <form onSubmit={handleSave} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Full Name</label>
                  <div className="relative">
                    <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type="text" value={profile.full_name || ''}
                      onChange={e => setProfile({ ...profile, full_name: e.target.value })}
                      required data-testid="profile-name" className={inputClass} />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type="tel" value={profile.phone || ''}
                      onChange={e => setProfile({ ...profile, phone: e.target.value })}
                      required data-testid="profile-phone" className={inputClass} />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Address</label>
                  <div className="relative">
                    <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input type="text" value={profile.address || ''}
                      onChange={e => setProfile({ ...profile, address: e.target.value })}
                      required data-testid="profile-address" className={inputClass} />
                  </div>
                </div>

                <button type="submit" disabled={saving} data-testid="profile-save"
                  className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold py-3.5 rounded-xl transition-all hover:shadow-lg hover:shadow-blue-200 hover:-translate-y-0.5 text-sm mt-2">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Save Changes</>}
                </button>
              </form>
            </div>

            {/* Note + Logout */}
            <p className="text-center text-xs text-slate-400">
              To change your PG, please contact JustFold support.
            </p>
            <button onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border border-red-100 text-red-500 hover:bg-red-50 font-semibold text-sm transition-all md:hidden">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </main>
      </div>

      <CustomerBottomNav />
    </div>
  );
};

export default CustomerProfile;
